// Shared data types. Every module imports from here; nothing here imports
// runtime code. If you need a new shared type, add it here rather than
// importing internals from another module.

// ───────────────────────────── IDs ─────────────────────────────

export type MechId = 'volt' | 'bastion' | 'wraith';
export const MECH_IDS: readonly MechId[] = ['volt', 'bastion', 'wraith'];

export type WeaponId =
  | 'pulse_carbine'
  | 'scatterforge'
  | 'arc_lance'
  | 'rail_driver'
  | 'micro_rockets'
  | 'gravity_launcher';
export const WEAPON_IDS: readonly WeaponId[] = [
  'pulse_carbine',
  'scatterforge',
  'arc_lance',
  'rail_driver',
  'micro_rockets',
  'gravity_launcher',
];

/** Weapon modifications (Arsenal orb). Some change the firing mode. */
export type ModId =
  | 'ionized'
  | 'incendiary'
  | 'fragmenting'
  | 'overclocked'
  | 'cryo_coil'
  | 'split_shot'
  | 'burst_fire'
  | 'heavy_rounds';
export const MOD_IDS: readonly ModId[] = [
  'ionized',
  'incendiary',
  'fragmenting',
  'overclocked',
  'cryo_coil',
  'split_shot',
  'burst_fire',
  'heavy_rounds',
];

export type EquipSlot = 'chest' | 'shoulders' | 'back' | 'legs';
export const EQUIP_SLOTS: readonly EquipSlot[] = ['chest', 'shoulders', 'back', 'legs'];

export type EquipId =
  // chest
  | 'reactive_plating'
  | 'ablative_shell'
  | 'capacitor_core'
  // shoulders
  | 'missile_pods'
  | 'storm_capacitor'
  | 'aegis_pauldrons'
  // back
  | 'overdrive_reactor'
  | 'tempest_reactor'
  | 'heat_sink_array'
  | 'jump_jets'
  // legs
  | 'vector_boosters'
  | 'cinder_treads'
  | 'servo_striders'
  | 'anchor_greaves';

export const EQUIP_SLOT_OF: Readonly<Record<EquipId, EquipSlot>> = {
  reactive_plating: 'chest',
  ablative_shell: 'chest',
  capacitor_core: 'chest',
  missile_pods: 'shoulders',
  storm_capacitor: 'shoulders',
  aegis_pauldrons: 'shoulders',
  overdrive_reactor: 'back',
  tempest_reactor: 'back',
  heat_sink_array: 'back',
  jump_jets: 'back',
  vector_boosters: 'legs',
  cinder_treads: 'legs',
  servo_striders: 'legs',
  anchor_greaves: 'legs',
};
export const EQUIP_IDS = Object.keys(EQUIP_SLOT_OF) as EquipId[];

/** Unstable-orb mutations. Some are mech-specific (see MutationDef.mech). */
export type MutationId =
  | 'chain_reactor'
  | 'glass_core'
  | 'meltdown_engine'
  | 'phase_capacitor'
  | 'volatile_payload'
  | 'feedback_loop'
  | 'magnet_heart'
  | 'overcharge'
  | 'berserk_servos'
  | 'arc_echo' // VOLT only
  | 'siege_lock' // BASTION only
  | 'ghost_protocol'; // WRAITH only
export const MUTATION_IDS: readonly MutationId[] = [
  'chain_reactor',
  'glass_core',
  'meltdown_engine',
  'phase_capacitor',
  'volatile_payload',
  'feedback_loop',
  'magnet_heart',
  'overcharge',
  'berserk_servos',
  'arc_echo',
  'siege_lock',
  'ghost_protocol',
];

/** Named tag-threshold combinations (see data/items.ts SYNERGIES). */
export type SynergyId =
  | 'storm_runner'
  | 'walking_fortress'
  | 'gravity_furnace'
  | 'conductor'
  | 'wildfire'
  | 'demolition'
  | 'bulwark'
  | 'afterburner';

/** Boss-reward "major upgrades" (choice of three after each sector boss). */
export type MajorUpgradeId =
  | 'core_reinforce'
  | 'twin_jets'
  | 'signature_evolve'
  | 'ultimate_amp'
  | 'heat_mastery'
  | 'mod_slot'
  | 'shield_matrix'
  | 'salvage_protocol'
  | 'dash_capacitor';

export type EnemyId = 'trooper' | 'hornet' | 'lancer' | 'bulwark';
export const ENEMY_IDS: readonly EnemyId[] = ['trooper', 'hornet', 'lancer', 'bulwark'];
export type EliteAffix = 'overcharged' | 'shielded' | 'volatile' | 'swift';
export const ELITE_AFFIXES: readonly EliteAffix[] = ['overcharged', 'shielded', 'volatile', 'swift'];

export type BossId = 'warden' | 'foundry_titan' | 'singularity_heart';
export type BiomeId = 'graveyard' | 'foundry' | 'vault';
export const BIOME_IDS: readonly BiomeId[] = ['graveyard', 'foundry', 'vault'];

export type SignatureId = 'arc_dash' | 'siege_field' | 'phase_echo';
export type UltimateId = 'overload' | 'fortress_protocol' | 'eclipse';
export type PassiveId = 'live_wire' | 'bulwark_frame' | 'ghost_drive';

export type OrbType = 'arsenal' | 'armor' | 'unstable';
export const ORB_TYPES: readonly OrbType[] = ['arsenal', 'armor', 'unstable'];
export type Rarity = 'standard' | 'advanced' | 'prototype';

/** Shared interaction tags. Synergies and item effects key off these. */
export type Tag =
  | 'electric'
  | 'burn'
  | 'dash'
  | 'shield'
  | 'explosive'
  | 'gravity'
  | 'kinetic'
  | 'precision'
  | 'heat'
  | 'missile'
  | 'chain';
export const TAGS: readonly Tag[] = [
  'electric',
  'burn',
  'dash',
  'shield',
  'explosive',
  'gravity',
  'kinetic',
  'precision',
  'heat',
  'missile',
  'chain',
];

export type TrailKind = 'electric' | 'burn' | 'plasma' | 'void';
export type StatusId = 'burn' | 'shock' | 'slow' | 'vulnerable' | 'stun' | 'overheated';
export type NodeType = 'start' | 'combat' | 'elite' | 'loot' | 'repair' | 'station' | 'boss';
export type LootSource = 'room' | 'elite' | 'boss' | 'loot_room' | 'shop' | 'crate' | 'challenge' | 'debug';

/** Meta-progression unlock id, e.g. 'mech:bastion', 'weapon:gravity_launcher', 'equip:jump_jets', 'cosmetic:volt:crimson', 'challenge:no_repairs'. */
export type UnlockId = string;

// ───────────────────────────── Animation / art metadata ─────────────────────────────

export type AnimKey = 'idle' | 'run' | 'jump' | 'dash' | 'shoot' | 'signature' | 'hurt' | 'death' | 'land';
export const ANIM_KEYS: readonly AnimKey[] = ['idle', 'run', 'jump', 'dash', 'shoot', 'signature', 'hurt', 'death', 'land'];
/** Frame counts per action sheet (76 per mech). */
export const ANIM_FRAMES: Readonly<Record<AnimKey, number>> = {
  idle: 8,
  run: 12,
  jump: 8,
  dash: 8,
  shoot: 6,
  signature: 12,
  hurt: 4,
  death: 12,
  land: 6,
};

/**
 * Attachment points stored per animation frame. Coordinates are pixels inside the
 * 128×128 cell for the RIGHT-facing pose; rot is radians (0 = pointing right,
 * positive = clockwise because screen y points down).
 */
export type AttachKey =
  | 'head'
  | 'chest'
  | 'back'
  | 'shoulderF' // front (near-camera) shoulder pivot: front arm + front shoulder module
  | 'shoulderR' // rear shoulder pivot: rear arm + rear shoulder module
  | 'hip'
  | 'shinF'
  | 'shinR'
  | 'footF'
  | 'footR';
export const ATTACH_KEYS: readonly AttachKey[] = [
  'head',
  'chest',
  'back',
  'shoulderF',
  'shoulderR',
  'hip',
  'shinF',
  'shinR',
  'footF',
  'footR',
];

export interface AttachPoint {
  x: number;
  y: number;
  rot: number;
}

/** Arm pose used when aimWeight < 1. Absolute angles in the right-facing frame. */
export interface ArmPose {
  upper: number;
  fore: number;
}

export interface MechFrameMeta {
  attach: Record<AttachKey, AttachPoint>;
  armF: ArmPose;
  armR: ArmPose;
  /** 1 = both arms track the aim direction (idle/run/jump); 0 = arms follow armF/armR (dash, death...). */
  aimWeight: number;
  /** Shoot sheet only: arm pull-back along the aim axis in px. */
  recoil?: number;
  /** Shoot sheet only: extra muzzle-rise rotation in radians (negative = up). */
  lift?: number;
}

export interface MechAnimInfo {
  /** Phaser animation key for the body sheet, e.g. 'volt_run'. */
  phaserKey: string;
  /** Frame index range inside the body sheet texture. */
  start: number;
  count: number;
  fps: number;
  loop: boolean;
  meta: MechFrameMeta[];
}

export interface MechArt {
  mechId: MechId;
  /** Body sheet (torso, head, legs; NO arms). Frames are numeric indices. */
  sheetKey: string;
  /** Same frame layout; emissive parts only, drawn white — tinted with the reactor colour and drawn additively. */
  glowSheetKey: string;
  anims: Record<AnimKey, MechAnimInfo>;
  arm: {
    upperKey: string;
    foreKey: string;
    /** Glow overlays for arm parts (white, tint at runtime). Optional. */
    upperGlowKey?: string;
    foreGlowKey?: string;
    upperLen: number;
    foreLen: number;
    /** Normalised origins (pivot at the shoulder / elbow). */
    upperOrigin: { x: number; y: number };
    foreOrigin: { x: number; y: number };
    /** Multiply tint for the rear (far) arm so it reads as further away. */
    rearTint: number;
  };
  /** Full composite sheet including arms (for preview/export only). */
  previewSheetKey?: string;
  /** Large hangar render of the neutral pose. */
  portraitKey: string;
  cell: number;
  /** Texels per rig unit of the body / glow / arm textures (sprites are drawn at 1 / sheetScale). */
  sheetScale?: number;
  footAnchor: { x: number; y: number };
}

export interface WeaponArt {
  key: string;
  /** Texels per weapon unit of the texture (the sprite is drawn at 1 / texScale). */
  texScale?: number;
  /** Normalised texture origin = the grip, which sits in the front hand. */
  origin: { x: number; y: number };
  /** Muzzle offset in px from the grip, weapon pointing right. */
  muzzle: { x: number; y: number };
  /** Where the rear hand reaches, px from the grip. */
  foregrip: { x: number; y: number };
  /**
   * Front-hand distance from the shoulder along the aim, as a fraction of the full arm
   * length (overrides the hold-style default; low = the gun is held close to the chest).
   */
  holdReach?: number;
  /** How far (px) the front hand sits below the shoulder→aim line (keeps a tall receiver below the face). */
  holdDrop?: number;
  /** Visual length in px (for UI). */
  length: number;
  /** Optional emissive overlay texture (white), same size/origin. */
  glowKey?: string;
  glowColor?: number;
}

/** One rigid module drawn at an attachment point. */
export interface VisualPiece {
  key: string;
  attach: AttachKey;
  /** 'rear' draws behind the body (and rear arm); 'front' draws above the body, below the front arm. */
  layer: 'rear' | 'front';
  /** Offset from the attachment point in px (right-facing frame, rotated by the point's rot if followRot). */
  dx: number;
  dy: number;
  /** Extra rotation in radians. */
  rot: number;
  originX: number;
  originY: number;
  /** If true the piece rotates with the attachment point. */
  followRot: boolean;
  scale?: number;
  /** Optional emissive overlay. */
  glowKey?: string;
}

export interface EquipVisual {
  pieces: VisualPiece[];
  dashTrail?: TrailKind;
  /** Optional override for the reactor glow colour while equipped. */
  reactorGlow?: number;
}

// ───────────────────────────── Stats ─────────────────────────────

export interface StatBlock {
  /** Absolute hit points. */
  maxHp: number;
  /** Absolute shield points (absorbs damage first, regenerates). */
  maxShield: number;
  /** Shield points per second once regenerating. */
  shieldRegen: number;
  /** Seconds without taking damage before shields regenerate. */
  shieldDelay: number;
  /** Fraction of HP damage ignored (0..0.6, clamp). */
  armor: number;
  /** Multiplier on MovementProfile.runSpeed. */
  moveSpeed: number;
  /** Multiplier on jump velocities. */
  jumpPower: number;
  /** Additive count of mid-air jumps. */
  airJumps: number;
  /** Additive count of dashes allowed before landing. */
  airDashes: number;
  /** Multiplier on dash cooldown (lower = faster). */
  dashCooldown: number;
  /** Multiplier on dash distance. */
  dashPower: number;
  /** Outgoing damage multiplier. */
  damage: number;
  /** Multiplier on weapon fire rate. */
  fireRate: number;
  /** Multiplier on weapon heat capacity (higher = more shots before overheat). */
  heatCapacity: number;
  /** Multiplier on weapon cooling rate. */
  cooling: number;
  /** 0..1 chance of a critical hit. */
  critChance: number;
  /** Critical damage multiplier. */
  critMult: number;
  /** Multiplier on signature cooldown (lower = faster). */
  signatureCooldown: number;
  /** Multiplier on ultimate charge gain. */
  ultimateCharge: number;
  /** Additive extra chain-lightning targets. */
  chainBonus: number;
  /** Multiplier on chain-lightning damage. */
  chainDamage: number;
  /** Multiplier on burn damage over time. */
  burnPower: number;
  /** Multiplier on explosion radius. */
  explosionRadius: number;
  /** Multiplier on explosion damage. */
  explosionDamage: number;
  /** Multiplier on knockback dealt. */
  knockback: number;
  /** 0..1 knockback received reduction. */
  knockbackResist: number;
  /** Pickup magnet radius in px. */
  pickupRadius: number;
  /** Multiplier on scrap gained. */
  scrapGain: number;
  /** Multiplier on incoming damage (tradeoffs such as Glass Core raise it). */
  damageTaken: number;
}
export type StatKey = keyof StatBlock;

export interface StatMod {
  stat: StatKey;
  op: 'add' | 'mul';
  value: number;
}

/** Per-mech movement feel. Speeds in px/s, accelerations in px/s², times in seconds. */
export interface MovementProfile {
  runSpeed: number;
  groundAccel: number;
  groundDecel: number;
  airAccel: number;
  airDecel: number;
  jumpVelocity: number;
  airJumpVelocity: number;
  gravity: number;
  /** Gravity multiplier while falling (snappier arcs). */
  fallGravityMult: number;
  /** Gravity multiplier near the apex while jump is held (hang time). */
  apexGravityMult: number;
  /** |vy| below which apex gravity applies. */
  apexThreshold: number;
  maxFall: number;
  /** vy multiplier applied when jump is released while rising (variable jump height). */
  jumpCutMult: number;
  /** Grace period to jump after leaving a ledge. */
  coyoteTime: number;
  /** Jump presses this long before landing still trigger. */
  jumpBuffer: number;
  dashSpeed: number;
  dashDuration: number;
  /** Invulnerability window from dash start. Must be shorter than dashDuration + dashRecovery. */
  dashIFrames: number;
  /** Reduced-control recovery after the dash. */
  dashRecovery: number;
  dashCooldown: number;
  /** Optional glide: holding jump while falling caps fall speed (WRAITH). */
  glideFallSpeed?: number;
}

// ───────────────────────────── Combat primitives ─────────────────────────────

export interface StatusApplication {
  id: StatusId;
  /** Stacks to add (burn/shock stack; others ignore). Default 1. */
  stacks?: number;
  /** Seconds. */
  duration: number;
  /** Status-specific strength (burn dps per stack, slow fraction, vulnerable bonus...). */
  potency?: number;
}

export interface StatusState {
  stacks: number;
  remaining: number;
  potency: number;
  /** Internal tick accumulator for DoTs. */
  tick: number;
}

export interface DamageInfo {
  amount: number;
  tags: Tag[];
  source: 'player' | 'enemy' | 'hazard' | 'self';
  /** Where the damage came from (for knockback direction and hit sparks). */
  sourceX?: number;
  sourceY?: number;
  /** Knockback impulse in px/s. */
  knockback?: number;
  /** Explicit knockback angle (radians); otherwise derived from source → target. */
  kbAngle?: number;
  weaponId?: WeaponId;
  crit?: boolean;
  /** Damage-over-time tick: no hit flash, no knockback, no on-hit triggers. */
  isDot?: boolean;
  statuses?: StatusApplication[];
  /** Bypass player i-frames (only for unavoidable sources such as self-damage). */
  ignoreIFrames?: boolean;
  /** Do not fire on-hit item triggers (prevents trigger recursion). */
  noTrigger?: boolean;
  /** True for repeats from echoes / secondary effects (no ultimate charge, reduced triggers). */
  secondary?: boolean;
  /** Free-form label for stats / debugging, e.g. 'arc_dash', 'contact'. */
  label?: string;
}

export interface DamageResult {
  dealt: number;
  killed: boolean;
  blocked: boolean;
  /** Portion absorbed by a shield layer. */
  absorbed: number;
}

// ───────────────────────────── Item definitions ─────────────────────────────

/** Firing behaviours implemented by systems/combat/weapons.ts. */
export type FireBehavior =
  | 'auto' // hold to fire projectiles at fireRate
  | 'spread' // pellets in a cone, range falloff, strong knockback
  | 'beam_chain' // short hitscan arc to the nearest target in a cone, chains to nearby enemies; ticks at fireRate
  | 'charge' // hold to charge (heat builds while charging), release fires a piercing shot scaled by charge
  | 'missile_burst' // one trigger pull fires burst.count homing missiles at burst.interval
  | 'lobber'; // gravity-affected grenade that bursts into a gravity field, then implodes

export interface WeaponDef {
  id: WeaponId;
  name: string;
  /** Two-word identity, e.g. 'Reliable automatic'. */
  short: string;
  desc: string;
  tags: Tag[];
  behavior: FireBehavior;
  holdStyle: 'rifle' | 'heavy' | 'compact';
  /** Damage per projectile / per beam tick / at full charge. */
  damage: number;
  /** Shots (or bursts, or beam ticks) per second. */
  fireRate: number;
  /** px/s; 0 for hitscan. */
  projectileSpeed: number;
  /** Total cone in radians. */
  spread: number;
  pellets: number;
  /** Max range in px. */
  range: number;
  /** Extra enemies a projectile passes through. */
  pierce: number;
  /** Knockback impulse px/s. */
  knockback: number;
  /** Heat per shot as a fraction of capacity (0..1). Charge weapons: heat per second while charging. */
  heatPerShot: number;
  /** Fraction of capacity cooled per second while not firing. */
  coolRate: number;
  /** Seconds after the last shot before cooling begins. */
  coolDelay: number;
  /** Seconds the weapon is locked (and the player vulnerable) after overheating. */
  overheatTime: number;
  chargeTime?: number;
  burst?: { count: number; interval: number };
  chain?: { count: number; range: number; falloff: number };
  explode?: { radius: number; damageMult: number };
  field?: { radius: number; duration: number; strength: number; implodeDamageMult: number };
  homing?: { turnRate: number; range: number; delay: number };
  /** Projectile gravity px/s² (lobber). */
  gravity?: number;
  projectile: { texture: string; radius: number; scale?: number; tint?: number };
  muzzleColor: number;
  sfx: SfxId;
  /** Visual recoil kick 0..1. */
  recoil: number;
  /** Camera shake intensity per shot (before settings scaling). */
  shake: number;
  /** Loot weight. */
  weight: number;
  unlock?: UnlockId;
}

/** Weapon-mod effects applied by the weapon runtime. All optional. */
export interface WeaponModifier {
  damageMult?: number;
  fireRateMult?: number;
  heatMult?: number;
  coolMult?: number;
  projectileSpeedMult?: number;
  knockbackMult?: number;
  spreadAdd?: number;
  pierceAdd?: number;
  /** Extra projectiles per shot fanned around the aim (split shot). */
  extraProjectiles?: number;
  /** Converts single shots into bursts (alternate firing mode). */
  burst?: { count: number; interval: number };
  addTags?: Tag[];
  addStatuses?: StatusApplication[];
  /** Kills by this weapon explode. */
  explodeOnKill?: { radius: number; damageMult: number };
}

export interface ModDef {
  id: ModId;
  name: string;
  desc: string;
  tags: Tag[];
  weapon: WeaponModifier;
  /** Human-readable tradeoff, if any. */
  tradeoff?: string;
  /** True if it changes the firing mode (shown as 'ALT FIRE MODE'). */
  altMode?: boolean;
  weight: number;
}

export type Trigger =
  | 'onDash'
  | 'onDashEnd'
  | 'onJump'
  | 'onLand'
  | 'onHit'
  | 'onKill'
  | 'onCrit'
  | 'onOverheat'
  | 'onVentEnd'
  | 'onDamaged'
  | 'onShieldBreak'
  | 'onSignature'
  | 'onUltimate'
  | 'onFire'
  | 'onRoomStart'
  | 'onRoomClear'
  | 'tick';

export type EffectAction =
  | { type: 'explode'; radius: number; damage: number; tags?: Tag[] }
  | { type: 'chain'; count: number; range: number; damage: number }
  | { type: 'shockwave'; radius: number; damage: number; knockback: number }
  | { type: 'missiles'; count: number; damage: number }
  | { type: 'status'; status: StatusApplication; radius?: number }
  | { type: 'shield'; amount: number }
  | { type: 'heal'; amount: number }
  | { type: 'cool'; amount: number }
  | { type: 'trail'; kind: TrailKind; dps: number; duration: number; status?: StatusApplication }
  | { type: 'gravity'; radius: number; duration: number; strength: number }
  | { type: 'cooldown'; ability: 'signature' | 'dash'; seconds: number }
  | { type: 'buff'; stat: StatKey; op: 'add' | 'mul'; value: number; duration: number }
  | { type: 'custom'; id: string; params?: Record<string, number> };

export interface ItemEffect {
  trigger: Trigger;
  action: EffectAction;
  /** 0..1, default 1. */
  chance?: number;
  /** Internal cooldown in seconds. */
  cooldown?: number;
  /** tick trigger: interval in seconds. */
  interval?: number;
  /** Only fire when the triggering damage carries this tag (onHit/onKill). */
  requiresTag?: Tag;
}

export interface EquipDef {
  id: EquipId;
  slot: EquipSlot;
  name: string;
  desc: string;
  tags: Tag[];
  stats: StatMod[];
  effects: ItemEffect[];
  /** Human-readable drawback (tradeoffs give identity; not every item is a pure upgrade). */
  tradeoff?: string;
  dashTrail?: TrailKind;
  weight: number;
  unlock?: UnlockId;
}

export interface MutationDef {
  id: MutationId;
  name: string;
  desc: string;
  drawback: string;
  tags: Tag[];
  stats: StatMod[];
  effects: ItemEffect[];
  /** Restricts the mutation to one mech (drops are always usable by the selected mech). */
  mech?: MechId;
  /** Runtime behaviour flags checked by code (e.g. 'ARC_ECHO'). */
  flags?: string[];
  /** Aura particle colour while owned. */
  aura?: number;
  weight: number;
}

export interface SynergyDef {
  id: SynergyId;
  name: string;
  desc: string;
  /** Minimum tag counts across all equipped items, mutations and the active mech kit. */
  requires: Partial<Record<Tag, number>>;
  stats?: StatMod[];
  effects?: ItemEffect[];
  /** Runtime behaviour flags checked by code, e.g. 'GRAVITY_BURN'. */
  flags?: string[];
  color: number;
}

export interface MajorUpgradeDef {
  id: MajorUpgradeId;
  name: string;
  desc: string;
  stats?: StatMod[];
  flags?: string[];
  /** Mech restriction (e.g. signature evolutions). */
  mech?: MechId;
}

/** Small rolled bonus on advanced/prototype items (identity, not just damage). */
export interface AffixInstance {
  id: string;
  label: string;
  stats?: StatMod[];
  /** Negative affix (prototype drawback). */
  negative?: boolean;
}

// ───────────────────────────── Item instances ─────────────────────────────

export type ItemKind = 'weapon' | 'equipment' | 'mod' | 'mutation';

interface ItemBase {
  /** Unique per instance. */
  uid: string;
  rarity: Rarity;
  affixes: AffixInstance[];
}
export interface WeaponInstance extends ItemBase {
  kind: 'weapon';
  defId: WeaponId;
  mods: ModInstance[];
  modSlots: number;
}
export interface EquipInstance extends ItemBase {
  kind: 'equipment';
  defId: EquipId;
  slot: EquipSlot;
}
export interface ModInstance extends ItemBase {
  kind: 'mod';
  defId: ModId;
}
export interface MutationInstance extends ItemBase {
  kind: 'mutation';
  defId: MutationId;
}
export type ItemInstance = WeaponInstance | EquipInstance | ModInstance | MutationInstance;

export interface StatDelta {
  label: string;
  /** Formatted value, e.g. '+12%' or '40 → 55'. */
  value: string;
  /** true = better, false = worse, null = neutral/sidegrade. */
  better: boolean | null;
}

export interface ItemComparison {
  item: ItemInstance;
  /** What it would replace (null = empty slot / addition). */
  current: ItemInstance | null;
  deltas: StatDelta[];
  gainedTags: Tag[];
  lostTags: Tag[];
  synergiesGained: SynergyId[];
  synergiesLost: SynergyId[];
  /** Would fill an empty slot automatically. */
  autoEquip: boolean;
}

/** Everything a card needs to draw an item. */
export interface ItemDisplay {
  name: string;
  kindLabel: string; // 'WEAPON', 'CHEST MODULE', 'WEAPON MOD', 'MUTATION'
  rarity: Rarity;
  rarityLabel: string;
  color: number;
  /** Short identity line. */
  short: string;
  /** Effect lines. */
  lines: string[];
  tradeoff?: string;
  tags: Tag[];
  icon: { key: string; frame?: string | number; tint?: number; scale?: number };
}

export interface ShopOffer {
  item: ItemInstance;
  price: number;
  sold: boolean;
}

// ───────────────────────────── World / run ─────────────────────────────

export interface RouteNode {
  id: string;
  type: NodeType;
  depth: number;
  /** Horizontal lane for map layout (0..lanes-1). */
  lane: number;
  next: string[];
  /** Chosen room template id (assigned at generation, seeded). */
  roomId: string;
  /** 0..1 difficulty within the sector. */
  difficulty: number;
  visited: boolean;
}

export interface RouteGraph {
  sector: number;
  nodes: Record<string, RouteNode>;
  startId: string;
  bossId: string;
  depthCount: number;
  lanes: number;
}

export interface RunStats {
  kills: number;
  eliteKills: number;
  bossKills: number;
  damageDealt: number;
  damageTaken: number;
  roomsCleared: number;
  orbsOpened: number;
  itemsFound: number;
  scrapEarned: number;
  challengesCompleted: number;
  timeSec: number;
  sectorReached: number;
  highestHit: number;
}

export interface PityState {
  /** Orbs opened since the last advanced-or-better reward. */
  sinceAdvanced: number;
  /** Arsenal orbs opened since a new weapon (not a mod). */
  sinceWeapon: number;
  /** Rewards since one that changed the build (new item id / filled slot / new synergy). */
  sinceUseful: number;
}

export interface MechDef {
  id: MechId;
  name: string;
  tagline: string;
  playstyle: string;
  reactorColor: number;
  glowColor: number;
  /**
   * Display size relative to the baked sheet (1 = as baked). Lets a mech read bigger or
   * smaller than its 128 px sheet cell allows; MechView applies it on top of any setScale().
   */
  viewScale?: number;
  base: StatBlock;
  movement: MovementProfile;
  passive: PassiveId;
  passiveName: string;
  passiveDesc: string;
  signature: SignatureId;
  signatureName: string;
  signatureDesc: string;
  /** Base signature cooldown in seconds. */
  signatureCooldown: number;
  ultimate: UltimateId;
  ultimateName: string;
  ultimateDesc: string;
  startingWeapon: WeaponId;
  /** Tags the mech kit contributes to synergy counts. */
  kitTags: Tag[];
  unlock?: UnlockId;
}

// ───────────────────────────── Audio ids ─────────────────────────────

export type SfxId =
  | 'ui_hover'
  | 'ui_click'
  | 'ui_back'
  | 'ui_confirm'
  | 'ui_deny'
  | 'jump'
  | 'double_jump'
  | 'land'
  | 'land_heavy'
  | 'dash'
  | 'footstep'
  | 'shoot_pulse'
  | 'shoot_scatter'
  | 'shoot_arc'
  | 'rail_charge'
  | 'shoot_rail'
  | 'shoot_rocket'
  | 'shoot_gravity'
  | 'overheat'
  | 'vent'
  | 'heat_warning'
  | 'weapon_switch'
  | 'hit_enemy'
  | 'hit_enemy_crit'
  | 'hit_shield'
  | 'hit_player'
  | 'shield_break'
  | 'shield_regen'
  | 'explosion_small'
  | 'explosion_large'
  | 'chain_zap'
  | 'burn_ignite'
  | 'gravity_field'
  | 'gravity_implode'
  | 'enemy_telegraph'
  | 'enemy_shoot'
  | 'enemy_laser_charge'
  | 'enemy_laser_fire'
  | 'enemy_death'
  | 'enemy_spawn'
  | 'drone_buzz'
  | 'boss_roar'
  | 'boss_slam'
  | 'boss_phase'
  | 'boss_death'
  | 'orb_open'
  | 'loot_reveal'
  | 'loot_rare'
  | 'equip'
  | 'salvage'
  | 'scrap_pickup'
  | 'buy'
  | 'reroll'
  | 'heal'
  | 'door_unlock'
  | 'room_clear'
  | 'challenge_complete'
  | 'challenge_fail'
  | 'arc_dash'
  | 'siege_field'
  | 'phase_echo'
  | 'overload'
  | 'fortress_protocol'
  | 'eclipse'
  | 'ultimate_ready'
  | 'signature_ready'
  | 'synergy'
  | 'death_player'
  | 'run_victory';

/** Continuous sounds started/stopped by code. */
export type LoopId = 'orb_hum' | 'beam' | 'charge_hum' | 'boss_engine' | 'rain' | 'field_hum';

export type MusicId = 'menu' | 'hangar' | 'graveyard' | 'foundry' | 'vault' | 'boss' | 'results';

// ───────────────────────────── Settings / save ─────────────────────────────

export interface Settings {
  masterVolume: number;
  musicVolume: number;
  sfxVolume: number;
  /** 0..1 multiplier on all screen shake (0 disables). */
  screenShake: number;
  /** Replaces full-screen flashes with subtle tints and softens hit flashes. */
  reducedFlashing: boolean;
  damageNumbers: boolean;
  showFps: boolean;
  /** Gamepad stick deadzone 0..0.5. */
  deadzone: number;
  /** Testing: everything counts as unlocked (the save itself is untouched). */
  testMode: boolean;
}
